// Rings from anywhere in the app, not just from inside the matching chat room.
// CallController (mounted only on the chat room pages) already handles a call
// end-to-end once you're *in* that conversation — this banner is what makes
// sure you find out about the call in the first place if you're on the
// dashboard, CRM, or any other screen when someone calls you.
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { on as onHub, joinUser, respondToCall } from "../api/chatHub";
import { useAuth } from "../context/AuthContext";
import { getAuthUser } from "../utils/storage";
import "../css/GlobalIncomingCallBanner.css";

function isLawyerRole(roleType) {
  const r = String(roleType || "")
    .toLowerCase()
    .trim();
  return ["agent", "lawyer", "advocate"].includes(r);
}

export default function GlobalIncomingCallBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [call, setCall] = useState(null); // { sessionId, callerId, callerName, callType }

  const authUser = user || getAuthUser();
  const selfId = authUser?.userId || authUser?.id || null;
  const selfName = authUser?.fullName || authUser?.name || "";
  const lawyer = isLawyerRole(authUser?.roleType || authUser?.role);
  // The two chat rooms don't share URL conventions: the lawyer-side route
  // (/lawyer/chat/:id) treats :id as the session id, but the customer-side
  // route (/chat/:id) treats :id as the OTHER party's (lawyer's) id and
  // resolves/creates the session from that — so an incoming call to a
  // customer has to route by the caller's id, not the session id.
  const chatPath = call
    ? lawyer
      ? `/lawyer/chat/${call.sessionId}`
      : `/chat/${call.callerId}`
    : null;

  useEffect(() => {
    if (!selfId) return undefined;
    joinUser(selfId).catch(() => {});

    const offIncoming = onHub("IncomingCall", (payload) => {
      // Already on this exact conversation — CallController there owns the UI.
      const onMatchingChatPage =
        location.pathname === `/lawyer/chat/${payload.sessionId}` ||
        location.pathname === `/agent-chat/${payload.sessionId}` ||
        location.pathname === `/chat/${payload.callerId}`;
      if (onMatchingChatPage) return;
      setCall(payload);
    });

    const offResponse = onHub("CallResponse", (payload) => {
      setCall((c) =>
        c && String(c.sessionId) === String(payload.sessionId) ? null : c,
      );
    });

    const offEnded = onHub("CallEnded", (payload) => {
      setCall((c) =>
        c && String(c.sessionId) === String(payload.sessionId) ? null : c,
      );
    });

    return () => {
      offIncoming();
      offResponse();
      offEnded();
    };
  }, [selfId, location.pathname]);

  const handleAccept = useCallback(() => {
    if (!call || !chatPath) return;
    navigate(chatPath, { state: { incomingCall: call } });
    setCall(null);
  }, [call, chatPath, navigate]);

  const handleDecline = useCallback(() => {
    if (!call) return;
    respondToCall(call.sessionId, false, selfName).catch(() => {});
    setCall(null);
  }, [call, selfName]);

  if (!call) return null;

  return (
    <div className="gicb-banner" role="alert">
      <div className="gicb-pulse" />
      <div className="gicb-info">
        <p className="gicb-title">{call.callerName || "Incoming call"}</p>
        <p className="gicb-sub">
          {call.callType === "video" ? "Video call" : "Voice call"}
        </p>
      </div>
      <div className="gicb-actions">
        <button
          type="button"
          className="gicb-btn gicb-btn--decline"
          onClick={handleDecline}
          aria-label="Decline call"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
            <path
              d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08a1.003 1.003 0 01-.29-.7c0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .27-.11.52-.29.7l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"
              transform="rotate(135 12 12)"
            />
          </svg>
        </button>
        <button
          type="button"
          className="gicb-btn gicb-btn--accept"
          onClick={handleAccept}
          aria-label="Accept call"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
            <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
          </svg>
        </button>
      </div>
    </div>
  );
}
